import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '../hooks/useMediaQuery';
import useLocalStorage from '../hooks/useLocalStorage';
import { clearFileCacheEntry } from '../components/resources/viewer/viewers/FileResourceViewer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useConfig } from './ConfigContext';
import { getKey } from '../utils/tokenStorage';

const UIContext = createContext(null);

export const useUI = () => useContext(UIContext);

const getInitialContentPanelHeight = () => {
  const savedHeight = localStorage.getItem('platform_app_contentPanelHeight');
  if (savedHeight) {
    try {
      const parsedHeight = JSON.parse(savedHeight);
      if (parsedHeight === '50%') return '50%';
      const heightValue = parseInt(parsedHeight, 10);
      if (!isNaN(heightValue) && heightValue < (window.innerHeight - 100)) {
        return parsedHeight;
      }
    } catch (e) {
      // ignore error
    }
  }
  return '50%';
};

export const UIProvider = ({ children }) => {
  const { agentList } = useConfig();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 960px)');

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(() => {
    if (isMobile) return false;
    try {
      const stored = localStorage.getItem(getKey('leftSidebarOpen'));
      return stored !== null ? JSON.parse(stored) : true;
    } catch (e) {
      return true;
    }
  });

  const [rightSidebarOpen, setRightSidebarOpen] = useState(() => {
    if (isMobile) return false;
    try {
      const stored = localStorage.getItem(getKey('rightSidebarOpen'));
      return stored !== null ? JSON.parse(stored) : true;
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(getKey('leftSidebarOpen'), JSON.stringify(leftSidebarOpen));
    } catch (e) {}
  }, [leftSidebarOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(getKey('rightSidebarOpen'), JSON.stringify(rightSidebarOpen));
    } catch (e) {}
  }, [rightSidebarOpen]);

  useEffect(() => {
    if (isMobile) {
      setLeftSidebarOpen(false);
      setRightSidebarOpen(false);
    }
  }, [isMobile]);

  const [openResources, setOpenResources] = useState([]);
  const [activeResourceId, setActiveResourceId] = useState(null);
  const [contentPanelHeight, setContentPanelHeight] = useLocalStorage('contentPanelHeight', getInitialContentPanelHeight());
  
  // CHANGED: Persist minimized state to prevent flickering on reconnect/reload
  const [isContentMinimized, setIsContentMinimized] = useLocalStorage('contentPanelMinimized', false);
  
  const [previousContentHeight, setPreviousContentHeight] = useState(null);
  const [selectedAgentName, setSelectedAgentName] = useState(agentList[0].name);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({ resource: null, options: {} });
  const [isNavigationLocked, setIsNavigationLocked] = useState(false);
  const [navigationLockReason, setNavigationLockReason] = useState('');

  const selectedAgent = useMemo(() => {
    return agentList.find(agent => agent.name === selectedAgentName) || agentList[0];
  }, [selectedAgentName, agentList]);

  const handleSetSelectedAgent = useCallback((agentName) => {
    console.log("Setting selected agent:", agentName);
    setSelectedAgentName(agentName);
  }, []);

  const getAgentIcon = useCallback((agentName) => {
    const agent = agentList.find(a => a.name === agentName);
    return agent ? agent.icon : <SmartToyIcon />;
  }, [agentList]);

  const toggleLeftSidebar = useCallback(() => {
    setLeftSidebarOpen(prev => !prev);
  }, []);

  const toggleRightSidebar = useCallback(() => {
    setRightSidebarOpen(prev => !prev);
  }, []);

  // CHANGED: Added options parameter to support opening tabs without expanding the panel
  const openResourceTab = useCallback((resourceData, options = {}) => {
    if (!resourceData || !resourceData.id) {
      console.error("Cannot open tab: Invalid resource data provided (missing ID).", resourceData);
      return;
    }

    const resourceTypeForRegistry = resourceData.resource_type || resourceData.type || 'unknown';
    if (resourceTypeForRegistry === 'unknown') {
        console.warn(`Opening tab for resource ${resourceData.id} with unknown type. Default viewer will be used. Original data:`, resourceData);
    }

    const resourceToAddOrUpdate = {
        ...resourceData,
        type: resourceTypeForRegistry,
        resource_type: resourceData.resource_type || resourceTypeForRegistry
    };

    setOpenResources(prev => {
      if (prev.length === 0) {
        // Only set default height if we are actually showing the panel
        if (!options.keepMinimized) {
            setContentPanelHeight('50%');
        }
      }
      const existingIndex = prev.findIndex(res => String(res.id) === String(resourceToAddOrUpdate.id));
      if (existingIndex !== -1) {
        const updatedResources = [...prev];
        updatedResources[existingIndex] = { ...prev[existingIndex], ...resourceToAddOrUpdate };
        return updatedResources;
      }
      return [...prev, resourceToAddOrUpdate];
    });

    setActiveResourceId(String(resourceToAddOrUpdate.id));

    // CHANGED: Only force expansion if keepMinimized is not true
    if (!options.keepMinimized) {
        setIsContentMinimized(false);
        if (isMobile) {
          setLeftSidebarOpen(false);
          setRightSidebarOpen(false);
        }
    }
  }, [isMobile, setContentPanelHeight, setIsContentMinimized]);

  const closeResourceTab = useCallback((resourceIdToClose) => {
    const idToCloseStr = String(resourceIdToClose);
    setOpenResources(currentResources => {
        const indexToClose = currentResources.findIndex(res => String(res.id) === idToCloseStr);
        if (indexToClose === -1) return currentResources;

        const resourceToClose = currentResources[indexToClose];
        if (resourceToClose?.type === 'file') {
            clearFileCacheEntry(idToCloseStr);
        }

        const newResources = currentResources.filter(res => String(res.id) !== idToCloseStr);
        
        setActiveResourceId(currentActiveId => {
            if (String(currentActiveId) === idToCloseStr) {
                if (newResources.length > 0) {
                    const newIndex = indexToClose >= newResources.length ? newResources.length - 1 : indexToClose;
                    return String(newResources[newIndex].id);
                }
                return null;
            }
            return currentActiveId;
        });

        return newResources;
    });
  }, []);

  const setActiveResourceTab = useCallback((resourceId) => {
    const idStr = String(resourceId);
    setOpenResources(currentResources => {
        if (currentResources.some(res => String(res.id) === idStr)) {
            setActiveResourceId(idStr);
            setIsContentMinimized(false);
        } else if (currentResources.length > 0) {
            console.warn(`Attempted to activate non-existent tab ID: ${idStr}. Activating first tab instead.`);
            setActiveResourceId(String(currentResources[0].id));
            setIsContentMinimized(false);
        } else {
            setActiveResourceId(null);
        }
        return currentResources;
    });
  }, [setIsContentMinimized]);

  const closeAllResourceTabs = useCallback(() => {
    setOpenResources(prev => {
        prev.forEach(resource => {
            if (resource.type === 'file') {
                clearFileCacheEntry(resource.id);
            }
        });
        return [];
    });
    setActiveResourceId(null);
  }, []);

  const handleSetContentPanelHeight = useCallback((newHeight) => {
    if (typeof newHeight === 'string') {
      if (newHeight === '50%' || newHeight.endsWith('px') || newHeight.endsWith('%') || newHeight.endsWith('vh')) {
        setContentPanelHeight(newHeight);
      } else {
        setContentPanelHeight(`${newHeight}px`);
      }
    } else if (typeof newHeight === 'number') {
      setContentPanelHeight(`${newHeight}px`);
    }
  }, [setContentPanelHeight]);

  const toggleMinimize = useCallback(() => {
    setIsContentMinimized(prevMinimized => {
      const nextMinimized = !prevMinimized;
      if (!nextMinimized) {
        // Restore
        const restoreHeight = previousContentHeight || '50%';
        setContentPanelHeight(restoreHeight);
      } else {
        // Minimize
        const panelElement = document.getElementById('content-panel-container');
        if (panelElement) {
          setPreviousContentHeight(`${panelElement.offsetHeight}px`);
        }
      }
      return nextMinimized;
    });
  }, [previousContentHeight, setContentPanelHeight, setIsContentMinimized]);

  const openResourcePreview = useCallback((resource, options = {}) => {
    console.log("Opening resource preview modal:", resource, options);
    setPreviewModal({ resource, options });
  }, []);

  const closeResourcePreview = useCallback(() => {
    setPreviewModal({ resource: null, options: {} });
  }, []);

  const openSettingsDialog = useCallback(() => setIsSettingsDialogOpen(true), []);
  const closeSettingsDialog = useCallback(() => setIsSettingsDialogOpen(false), []);
  
  const openSubscriptionDialog = useCallback(() => setIsSubscriptionDialogOpen(true), []);
  const closeSubscriptionDialog = useCallback(() => setIsSubscriptionDialogOpen(false), []);

  const setNavigationLock = useCallback((locked, message = '') => {
    setIsNavigationLocked(locked);
    setNavigationLockReason(message);
  }, []);

  const contextValue = useMemo(() => ({
    leftSidebarOpen,
    rightSidebarOpen,
    toggleLeftSidebar,
    toggleRightSidebar,
    setLeftSidebarOpen,
    setRightSidebarOpen,
    isMobile,
    isTablet,
    openResources,
    activeResourceId,
    openResourceTab,
    closeResourceTab,
    setActiveResourceTab,
    closeAllResourceTabs,
    contentPanelHeight,
    isContentMinimized,
    setContentPanelHeight: handleSetContentPanelHeight,
    setIsContentMinimized,
    toggleMinimize,
    selectedAgent,
    setSelectedAgent: handleSetSelectedAgent,
    getAgentIcon,
    isSettingsDialogOpen,
    openSettingsDialog,
    closeSettingsDialog,
    isSubscriptionDialogOpen,
    openSubscriptionDialog,
    closeSubscriptionDialog,
    previewModal,
    openResourcePreview,
    closeResourcePreview,
    isNavigationLocked,
    navigationLockReason,
    setNavigationLock,
  }), [
    leftSidebarOpen,
    rightSidebarOpen,
    toggleLeftSidebar,
    toggleRightSidebar,
    setLeftSidebarOpen,
    setRightSidebarOpen,
    isMobile,
    isTablet,
    openResources,
    activeResourceId,
    openResourceTab,
    closeResourceTab,
    setActiveResourceTab,
    closeAllResourceTabs,
    contentPanelHeight,
    isContentMinimized,
    handleSetContentPanelHeight,
    setIsContentMinimized,
    toggleMinimize,
    selectedAgent,
    handleSetSelectedAgent,
    getAgentIcon,
    isSettingsDialogOpen,
    openSettingsDialog,
    closeSettingsDialog,
    isSubscriptionDialogOpen,
    openSubscriptionDialog,
    closeSubscriptionDialog,
    previewModal,
    openResourcePreview,
    closeResourcePreview,
    isNavigationLocked,
    navigationLockReason,
    setNavigationLock,
  ]);

  return (
    <UIContext.Provider value={contextValue}>
      {children}
    </UIContext.Provider>
  );
};

UIProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UIContext;